import OpenAI from 'openai';
import { DocumentaryScene, DocumentaryStyle } from './documentaryAnalyzer';

let openaiClient: OpenAI | null = null;

export const initializeDocumentaryAI = (apiKey: string): boolean => {
  try {
    openaiClient = new OpenAI({
      apiKey: apiKey,
      dangerouslyAllowBrowser: true
    });
    return true;
  } catch (error) {
    console.error('Failed to initialize OpenAI for Documentary:', error);
    return false;
  }
};

export const isDocumentaryAIReady = (): boolean => {
  return openaiClient !== null;
};

interface AIDocumentaryAnalysis {
  genre: string;
  style: DocumentaryStyle;
  scenes: DocumentaryScene[];
  narrativeArc: string;
  visualTheme: string;
  keyMoments: string[];
}

export const analyzeDocumentaryWithAI = async (
  story: string,
  platform: 'midjourney' | 'veo' | 'universal' = 'midjourney'
): Promise<AIDocumentaryAnalysis> => {
  if (!openaiClient) throw new Error('Documentary AI not initialized');

  const systemPrompt = `You are an expert documentary filmmaker and AI prompt engineer. 
  Analyze the given story and create a professional documentary treatment with scene-by-scene visual prompts.
  
  For each scene, provide:
  1. A detailed visual description
  2. Camera work and cinematography
  3. Mood and atmosphere
  4. Specific prompts optimized for ${platform}
  
  Detect the documentary genre (comic, scientific, historical, nature, biographical, investigative, cultural)
  and adapt the visual style accordingly.
  
  Return a structured JSON response with the complete documentary breakdown.`;

  try {
    const completion = await openaiClient.chat.completions.create({
      model: "gpt-4-turbo-preview", // Latest GPT-4 model
      messages: [
        { role: "system", content: systemPrompt },
        { 
          role: "user", 
          content: `Create a documentary treatment for this story: "${story}"
          
          Return as JSON with this structure:
          {
            "genre": "detected genre",
            "visualTheme": "overall visual theme description",
            "narrativeArc": "story structure analysis",
            "keyMoments": ["list of key dramatic moments"],
            "scenes": [
              {
                "sceneNumber": 1,
                "title": "Scene title",
                "description": "What happens",
                "duration": "X seconds",
                "imagePrompt": "Complete prompt for image generation",
                "videoPrompt": "Complete prompt for video generation",
                "cameraWork": "Camera technique",
                "mood": "Emotional tone",
                "soundscape": "Audio description"
              }
            ]
          }`
        }
      ],
      ...(typeof model !== 'undefined' && (model === 'gpt-3.5-turbo' || model === 'gpt-4-turbo-preview' || model === 'gpt-4o' || model === 'gpt-4o-mini' || model === 'gpt-5' || model === 'gpt-5-mini') ? { response_format: { type: "json_object" } } : {}),
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.3 }),
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 2000 } : { max_tokens: 2000 })
    });

    const response = JSON.parse(completion.choices[0].message.content || '{}');
    
    // Map to our structure
    const analysis: AIDocumentaryAnalysis = {
      genre: response.genre || 'universal',
      style: mapGenreToStyle(response.genre),
      scenes: enhanceScenePrompts(response.scenes || [], platform, response.genre),
      narrativeArc: response.narrativeArc || '',
      visualTheme: response.visualTheme || '',
      keyMoments: response.keyMoments || []
    };

    return analysis;
  } catch (error) {
    console.error('Error analyzing documentary:', error);
    throw error;
  }
};

export const generateCinematicScenes = async (
  story: string,
  genre: string,
  sceneCount: number = 5,
  platform: 'midjourney' | 'veo' | 'universal' = 'midjourney'
): Promise<DocumentaryScene[]> => {
  if (!openaiClient) throw new Error('Documentary AI not initialized');

  const styleGuide = getCinematicStyleGuide(genre, platform);
  
  const systemPrompt = `You are a master cinematographer and AI prompt specialist.
  Create ${sceneCount} cinematic scenes for a ${genre} documentary.
  
  Style Guide:
  ${styleGuide}
  
  Each scene must have:
  - Unique visual composition
  - Specific camera movements
  - Emotional progression
  - Technical parameters for ${platform}
  
  Focus on creating visually stunning, emotionally impactful scenes that tell a cohesive story.`;

  try {
    const completion = await openaiClient.chat.completions.create({
      model: "gpt-4-turbo-preview",
      messages: [
        { role: "system", content: systemPrompt },
        { 
          role: "user", 
          content: `Story: "${story}"
          
          Generate ${sceneCount} scenes with this exact JSON structure:
          [
            {
              "sceneNumber": 1,
              "title": "Descriptive title",
              "description": "Detailed scene description",
              "duration": "X-Y seconds",
              "imagePrompt": "Complete ${platform} prompt with all parameters",
              "videoPrompt": "Complete video generation prompt",
              "cameraWork": "Specific camera technique (e.g., 'Slow dolly zoom with rack focus')",
              "mood": "Emotional tone and atmosphere",
              "soundscape": "Detailed audio landscape",
              "lighting": "Lighting setup description",
              "colorGrading": "Color treatment",
              "transitionTo": "How this scene transitions to the next"
            }
          ]`
        }
      ],
      ...(typeof model !== 'undefined' && (model === 'gpt-3.5-turbo' || model === 'gpt-4-turbo-preview' || model === 'gpt-4o' || model === 'gpt-4o-mini' || model === 'gpt-5' || model === 'gpt-5-mini') ? { response_format: { type: "json_object" } } : {}),
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.3 }),
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 3000 } : { max_tokens: 3000 })
    });

    const response = JSON.parse(completion.choices[0].message.content || '{"scenes": []}');
    const scenes = response.scenes || response;
    
    return Array.isArray(scenes) ? scenes : [];
  } catch (error) {
    console.error('Error generating cinematic scenes:', error);
    throw error;
  }
};

export const optimizePromptForPlatform = async (
  basePrompt: string,
  platform: 'midjourney' | 'veo' | 'stable-diffusion' | 'dall-e'
): Promise<string> => {
  if (!openaiClient) throw new Error('Documentary AI not initialized');

  const platformSpecs = {
    midjourney: 'Add parameters: --ar (aspect ratio), --v 7 (version), --style raw, --q 2 (quality), --stylize (0-1000)',
    veo: 'Add duration (seconds), camera movement, frame rate, resolution (4K/8K)',
    'stable-diffusion': 'Add sampling method, CFG scale, steps, negative prompts',
    'dall-e': 'Focus on clear, detailed descriptions, avoid parameters'
  };

  try {
    const completion = await openaiClient.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { 
          role: "system", 
          content: `Optimize prompts for ${platform}. ${platformSpecs[platform]}` 
        },
        { 
          role: "user", 
          content: `Optimize this documentary prompt for ${platform}: "${basePrompt}"` 
        }
      ],
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.3 }),
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 300 } : { max_tokens: 300 })
    });

    return completion.choices[0].message.content || basePrompt;
  } catch (error) {
    console.error('Error optimizing prompt:', error);
    return basePrompt;
  }
};

export const generateStoryboard = async (
  scenes: DocumentaryScene[],
  title: string = 'Documentary'
): Promise<string> => {
  if (!openaiClient) throw new Error('Documentary AI not initialized');

  try {
    const completion = await openaiClient.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { 
          role: "system", 
          content: 'Create a professional documentary storyboard document in markdown format.' 
        },
        { 
          role: "user", 
          content: `Create a storyboard for "${title}" with these scenes: ${JSON.stringify(scenes)}
          
          Include:
          - Executive summary
          - Visual treatment notes
          - Scene-by-scene breakdown
          - Technical specifications
          - Production notes`
        }
      ],
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.5 }),
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 2000 } : { max_tokens: 2000 })
    });

    return completion.choices[0].message.content || '';
  } catch (error) {
    console.error('Error generating storyboard:', error);
    return '';
  }
};

// Helper functions
const mapGenreToStyle = (genre: string): DocumentaryStyle => {
  const styleMap: Record<string, DocumentaryStyle> = {
    comic: {
      genre: 'comic',
      visualStyle: 'vibrant comic book aesthetic, cel-shaded, bold outlines, exaggerated expressions',
      colorPalette: 'bright primary colors, high saturation, comic book palette',
      cameraStyle: 'dynamic angles, Dutch tilts, comic panel transitions',
      pacing: 'fast cuts, energetic transitions',
      narrativeTone: 'humorous, lighthearted, entertaining'
    },
    scientific: {
      genre: 'scientific',
      visualStyle: 'photorealistic, ultra-detailed, scientific accuracy, pristine clarity',
      colorPalette: 'clean whites, technological blues, laboratory lighting',
      cameraStyle: 'steady shots, macro details, time-lapse sequences',
      pacing: 'measured, methodical',
      narrativeTone: 'educational, authoritative, wonder-inducing'
    },
    historical: {
      genre: 'historical',
      visualStyle: 'period-authentic, cinematic grandeur, epic scale',
      colorPalette: 'desaturated historical tones, sepia accents',
      cameraStyle: 'sweeping crane shots, steadicam walks, documentary realism',
      pacing: 'deliberate, contemplative',
      narrativeTone: 'reverent, dramatic, educational'
    },
    nature: {
      genre: 'nature',
      visualStyle: 'BBC Planet Earth quality, ultra-high definition, natural beauty',
      colorPalette: 'vibrant natural colors, earth tones, sky blues',
      cameraStyle: 'wildlife cinematography, aerial drones, macro lenses',
      pacing: 'patient, observational',
      narrativeTone: 'awe-inspiring, environmental, poetic'
    },
    biographical: {
      genre: 'biographical',
      visualStyle: 'intimate portraiture, personal moments, authentic environments',
      colorPalette: 'warm human tones, soft natural light',
      cameraStyle: 'handheld intimacy, close-ups, interview setups',
      pacing: 'emotionally driven',
      narrativeTone: 'personal, inspiring, human'
    },
    investigative: {
      genre: 'investigative',
      visualStyle: 'gritty realism, documentary noir, high contrast',
      colorPalette: 'dark shadows, stark contrasts, moody atmosphere',
      cameraStyle: 'handheld urgency, surveillance aesthetic, hidden cameras',
      pacing: 'tense, suspenseful',
      narrativeTone: 'serious, probing, revelatory'
    },
    cultural: {
      genre: 'cultural',
      visualStyle: 'vibrant cultural authenticity, traditional aesthetics',
      colorPalette: 'rich cultural colors, ceremonial hues',
      cameraStyle: 'immersive observation, respectful distance',
      pacing: 'rhythmic, ceremonial',
      narrativeTone: 'respectful, celebratory, educational'
    }
  };

  return styleMap[genre.toLowerCase()] || styleMap.scientific;
};

const enhanceScenePrompts = (
  scenes: any[],
  platform: string,
  genre: string
): DocumentaryScene[] => {
  return scenes.map((scene, index) => {
    const enhancedScene: DocumentaryScene = {
      sceneNumber: scene.sceneNumber || index + 1,
      title: scene.title || `Scene ${index + 1}`,
      description: scene.description || '',
      duration: scene.duration || '5-8 seconds',
      imagePrompt: addPlatformParameters(scene.imagePrompt || '', platform, 'image'),
      videoPrompt: addPlatformParameters(scene.videoPrompt || '', platform, 'video'),
      cameraWork: scene.cameraWork || 'medium shot',
      mood: scene.mood || 'atmospheric',
      soundscape: scene.soundscape || 'ambient documentary score'
    };
    
    return enhancedScene;
  });
};

const addPlatformParameters = (
  prompt: string,
  platform: string,
  type: 'image' | 'video'
): string => {
  if (platform === 'midjourney' && type === 'image') {
    if (!prompt.includes('--ar')) prompt += ' --ar 16:9';
    if (!prompt.includes('--v')) prompt += ' --v 7';
    if (!prompt.includes('--style')) prompt += ' --style raw';
    if (!prompt.includes('--q')) prompt += ' --q 2';
  } else if (platform === 'veo' && type === 'video') {
    if (!prompt.includes('seconds')) prompt += ', 10 seconds';
    if (!prompt.includes('4K') && !prompt.includes('8K')) prompt += ', 4K resolution';
  }
  
  return prompt;
};

const getCinematicStyleGuide = (genre: string, platform: string): string => {
  const guides: Record<string, string> = {
    comic: `
      Visual Style: Comic book aesthetics, vibrant colors, dynamic action
      Camera: Dutch angles, crash zooms, whip pans
      Mood: Energetic, playful, exaggerated
      ${platform === 'midjourney' ? 'Parameters: --stylize 750 --chaos 25' : ''}
    `,
    scientific: `
      Visual Style: Ultra-realistic, pristine clarity, scientific precision
      Camera: Smooth tracking, macro details, steady movements
      Mood: Wonder, discovery, educational
      ${platform === 'midjourney' ? 'Parameters: --stylize 100 --quality 2' : ''}
    `,
    historical: `
      Visual Style: Period authentic, epic scale, cinematic grandeur
      Camera: Sweeping crane shots, steadicam walks
      Mood: Dramatic, reverent, powerful
      ${platform === 'midjourney' ? 'Parameters: --stylize 500 --quality 2' : ''}
    `,
    nature: `
      Visual Style: BBC Planet Earth quality, natural beauty
      Camera: Wildlife lenses, aerial drones, time-lapse
      Mood: Majestic, peaceful, awe-inspiring
      ${platform === 'midjourney' ? 'Parameters: --stylize 250 --quality 2' : ''}
    `
  };
  
  return guides[genre] || guides.scientific;
};

// Viral Story Types
export interface ViralStoryIdea {
  number: number;
  title: string;
  teaser?: string;
  description?: string;
  storyPotential?: string;
}

export interface ExpandedReason {
  title: string;
  description: string;
  viralPotential?: string;
  emotionalHook?: string;
  visualElements?: string[];
}

export interface ViralStory {
  fullStory: string;
  parts: {
    boldHook: string;
    relatableAnalogy: string;
    stepByStepReveal: string;
    contrastAndDrama: string;
    emotionalTieIn: string;
    mysteryEnding: string;
    softCTA: string;
  };
  metadata: {
    hookType: string;
    emotionalTone: string;
    targetAudience: string;
    estimatedReadTime: string;
    platformOptimization: string[];
    aiModel?: string;
  };
}

// Generate viral story ideas
export const generateViralStoryIdeas = async (
  topic: string,
  apiKey: string
): Promise<ViralStoryIdea[]> => {
  if (!apiKey) throw new Error('API key required');
  
  const client = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true
  });

  const systemPrompt = `You are a viral content expert who understands what makes stories spread online.
  Generate 10 unusual and fascinating reasons why this topic matters.
  Each reason should be surprising, emotionally engaging, and make people want to share.`;

  const userPrompt = `Generate 10 unusual reasons why "${topic}" is important.
  Return as JSON array with format:
  [
    {
      "number": 1,
      "title": "Shocking Reason Title",
      "teaser": "Brief teaser that creates curiosity",
      "description": "2-3 sentence explanation",
      "storyPotential": "High/Medium/Low viral potential"
    }
  ]`;

  try {
    const completion = await client.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt }
      ],
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.3 }),
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 1500 } : { max_tokens: 1500 }),
      response_format: { type: "json_object" }
    });

    const response = JSON.parse(completion.choices[0].message.content || '{"reasons":[]}');
    return response.reasons || response || [];
  } catch (error) {
    console.error('Error generating story ideas:', error);
    throw error;
  }
};

// Expand story reason
export const expandStoryReason = async (
  topic: string,
  reason: ViralStoryIdea,
  apiKey: string
): Promise<ExpandedReason[]> => {
  if (!apiKey) throw new Error('API key required');
  
  const client = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true
  });

  const systemPrompt = `You are a master storyteller who creates viral content.
  Generate 5 SPECIFIC sub-stories that are DIRECTLY RELATED to the given angle.
  Each must be a different aspect of the SAME main topic.`;

  const userPrompt = `Generate 5 SPECIFIC sub-stories for this angle about "${topic}":
  MAIN ANGLE: ${reason.title}
  ANGLE DESCRIPTION: ${reason.teaser || reason.description}
  STORY ANGLE: ${reason.storyAngle || ''}
  
  CRITICAL: All 5 stories MUST be about "${reason.title}" specifically!
  They should explore different aspects of THIS EXACT ANGLE, not other unrelated topics.
  
  For example, if the angle is "Titanic's Final Frontier", all 5 stories should be about the Titanic wreck itself.
  
  Return as JSON array with format:
  [
    {
      "title": "Compelling Story Angle",
      "description": "2-3 sentence description",
      "viralPotential": "High/Medium",
      "emotionalHook": "The primary emotion it triggers",
      "visualElements": ["element1", "element2"]
    }
  ]`;

  try {
    const completion = await client.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt }
      ],
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.3 }),  // Lower for simpler language
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 1500 } : { max_tokens: 1500 }),
      response_format: { type: "json_object" }
    });

    const response = JSON.parse(completion.choices[0].message.content || '{"angles":[]}');
    return response.angles || response || [];
  } catch (error) {
    console.error('Error expanding story reason:', error);
    throw error;
  }
};

// Create viral story
export const createViralStory = async (
  topic: string,
  idea: ExpandedReason,
  apiKey: string
): Promise<ViralStory> => {
  if (!apiKey) throw new Error('API key required');
  
  const client = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true
  });

  const systemPrompt = `You write viral TikTok documentaries. Study these EXACT winning examples:
  
  WINNING STYLE EXAMPLES:
  "The water you're drinking right now might have once been inside a Dinosaur."
  "Your teeth tear it apart, your saliva soaks it."
  "Two particles can stay connected even if they're galaxies apart."
  "It's ancient. It doesn't vanish. It doesn't get replaced."
  
  CRITICAL RULES:
  1. Write EXACTLY like the examples above - short, punchy, conversational
  2. Most sentences: 5-12 words (some can be 2-3 words for impact)
  3. Use everyday words people actually say
  4. No fancy vocabulary - write how you talk to friends
  5. Create visual, concrete images
  6. Use "you" and "your" to make it personal
  
  GOOD WORDS: ancient, crazy, wild, weird, real, actual, insane
  BAD WORDS: magnificent, extraordinary, phenomenal, intricate
  
  RHYTHM PATTERN:
  - Statement. Short reaction. Expand the idea.
  - "No, really." "Think about it." "Here's the thing."
  - Build momentum with varying sentence lengths
  
  Write EXACTLY like successful TikTok scripts. Total: 220-260 words.`;

  const userPrompt = `Create a viral TikTok documentary story about this SPECIFIC phenomenon:
  TITLE: "${idea.title}"  
  DESCRIPTION: ${idea.description}
  
  IMPORTANT: Focus ONLY on the specific idea above, NOT the general topic "${topic}".
  
  Write a complete 220-260 word documentary story following this EXACT 7-part structure:
  
  1. Bold Hook (20-30 words) - Start with a simple, shocking fact
  2. Relatable Analogy (25-35 words) - Use everyday comparisons
  3. Step-by-Step Reveal (40-50 words) - Build with simple facts
  4. Contrast & Drama (40-50 words) - Show opposites clearly
  5. Emotional Tie-In (35-45 words) - Connect to feelings
  6. Mystery Ending (30-40 words) - Ask a simple question
  7. Soft CTA (20-30 words) - "Follow for more facts"
  
  COPY THIS EXACT STYLE:
  "Your brain holds the internet. No, really."
  "Go down 20 meters. The ocean is 100 times deeper."
  "Water exists in a closed system."
  "You're not just drinking water. You're sipping Earth's memory."
  
  SENTENCE VARIETY:
  - Impact line: 3-5 words ("No, really." "Think about it.")
  - Main point: 8-12 words (natural speaking rhythm)
  - Visual example: 10-15 words (paint a picture)
  
  Never use academic or scientific language. Write like you're telling a friend an amazing fact.
  
  CRITICAL INSTRUCTION:
  The "fullStory" field MUST contain the COMPLETE story (all 7 parts combined into one flowing 220-260 word narrative).
  Do NOT leave fullStory empty or partial. Combine all parts into one complete story.
  
  Return as JSON with this EXACT structure:
  {
    "fullStory": "[INSERT THE COMPLETE 220-260 WORD STORY HERE - ALL 7 PARTS COMBINED]",
    "parts": {
      "boldHook": "Hook text only",
      "relatableAnalogy": "Analogy text only",
      "stepByStepReveal": "Reveal text only",
      "contrastAndDrama": "Drama text only",
      "emotionalTieIn": "Emotional text only",
      "mysteryEnding": "Mystery text only",
      "softCTA": "CTA text only"
    },
    "metadata": {
      "topic": "${topic}",
      "wordCount": [actual count of fullStory],
      "emotionalTone": "Primary emotion",
      "estimatedReadTime": "55 seconds",
      "targetPlatform": ["TikTok", "Instagram", "YouTube Shorts"]
    }
  }`;

  try {
    const completion = await client.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt }
      ],
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.3 }),  // Lower for simpler language
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 2500 } : { max_tokens: 2500 }),
      response_format: { type: "json_object" }
    });

    const story = JSON.parse(completion.choices[0].message.content || '{}');
    
    // Ensure fullStory is complete - if it's missing or too short, combine the parts
    if (!story.fullStory || story.fullStory.length < 100) {
      story.fullStory = [
        story.parts?.boldHook || '',
        story.parts?.relatableAnalogy || '',
        story.parts?.stepByStepReveal || '',
        story.parts?.contrastAndDrama || '',
        story.parts?.emotionalTieIn || '',
        story.parts?.mysteryEnding || '',
        story.parts?.softCTA || ''
      ].filter(part => part).join(' ');
    }
    
    // Ensure metadata has required fields
    if (!story.metadata) {
      story.metadata = {};
    }
    if (!story.metadata.wordCount) {
      story.metadata.wordCount = story.fullStory.split(/\s+/).length;
    }
    if (!story.metadata.estimatedReadTime) {
      story.metadata.estimatedReadTime = `${Math.ceil(story.metadata.wordCount / 150 * 60)} seconds`;
    }
    if (!story.metadata.emotionalTone) {
      story.metadata.emotionalTone = 'Awe and wonder';
    }
    if (!story.metadata.targetPlatform) {
      story.metadata.targetPlatform = ['TikTok', 'Instagram', 'YouTube Shorts'];
    }
    
    return story;
  } catch (error) {
    console.error('Error creating viral story:', error);
    throw error;
  }
};

// Regenerate story with ChatGPT
export const regenerateStoryWithChatGPT = async (
  topic: string,
  idea: ExpandedReason,
  apiKey: string,
  currentStory?: string,
  model: string = 'gpt-3.5-turbo'
): Promise<ViralStory> => {
  if (!apiKey) throw new Error('API key required');
  
  const client = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true
  });

  const systemPrompt = `You are ChatGPT writing viral TikTok documentaries.
  
  ${currentStory ? `IMPORTANT: You must maintain the EXACT same story structure and content.
  Current story to rewrite in ChatGPT style:
  "${currentStory}"
  
  Keep ALL the same facts, numbers, and examples.
  Only change the VOICE to ChatGPT style (conversational, friendly).
  DO NOT change the story content or structure.` : ''}
  
  CHATGPT STYLE RULES:
  - Conversational and friendly but mind-blowing
  - Use "you" to connect directly
  - Short sentences (5-12 words mostly)
  - Simple words everyone uses daily
  - Add reactions: "No, really." "Think about that."
  
  Keep the exact same 7-part structure and content, just change the voice.`;

  const userPrompt = `Rewrite this viral story for: ${topic}
  Angle: ${idea.title}: ${idea.description}
  
  Use the 7-part viral structure but in YOUR unique ChatGPT voice:
  - More conversational and friendly
  - Use "you" and "we" to connect
  - Add personal touches
  - Keep the wonder but make it accessible
  
  Return JSON with this EXACT structure:
  {
    "fullStory": "Complete 220-260 word story",
    "parts": {
      "boldHook": "Hook text",
      "relatableAnalogy": "Analogy text",
      "stepByStepReveal": "Reveal text",
      "contrastAndDrama": "Drama text",
      "emotionalTieIn": "Emotional text",
      "mysteryEnding": "Mystery text",
      "softCTA": "CTA text"
    },
    "metadata": {
      "wordCount": 250,
      "emotionalTone": "Conversational",
      "estimatedReadTime": "55 seconds",
      "targetPlatform": ["TikTok", "Instagram", "YouTube Shorts"]
    }
  }`;

  try {
    const completion = await client.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt }
      ],
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.3 }),
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 2500 } : { max_tokens: 2500 }),
      response_format: { type: "json_object" }
    });

    const story = JSON.parse(completion.choices[0].message.content || '{}');
    
    // Ensure all required parts exist
    if (!story.parts) {
      story.parts = {
        boldHook: '',
        relatableAnalogy: '',
        stepByStepReveal: '',
        contrastAndDrama: '',
        emotionalTieIn: '',
        mysteryEnding: '',
        softCTA: ''
      };
    }
    
    // Ensure fullStory exists
    if (!story.fullStory || story.fullStory.length < 100) {
      story.fullStory = [
        story.parts.boldHook || '',
        story.parts.relatableAnalogy || '',
        story.parts.stepByStepReveal || '',
        story.parts.contrastAndDrama || '',
        story.parts.emotionalTieIn || '',
        story.parts.mysteryEnding || '',
        story.parts.softCTA || ''
      ].filter(part => part).join(' ');
    }
    
    story.metadata = story.metadata || {};
    story.metadata.aiModel = 'ChatGPT';
    story.metadata.wordCount = story.metadata.wordCount || story.fullStory.split(/\s+/).length;
    story.metadata.estimatedReadTime = story.metadata.estimatedReadTime || '55 seconds';
    story.metadata.emotionalTone = story.metadata.emotionalTone || 'Conversational';
    story.metadata.targetPlatform = story.metadata.targetPlatform || ['TikTok', 'Instagram', 'YouTube Shorts'];
    
    return story;
  } catch (error) {
    console.error('Error regenerating with ChatGPT:', error);
    throw error;
  }
};

// Regenerate story with Google AI
export const regenerateStoryWithGoogleAI = async (
  topic: string,
  idea: ExpandedReason,
  apiKey: string,
  currentStory?: string,
  model: string = 'gpt-3.5-turbo'
): Promise<ViralStory> => {
  if (!apiKey) throw new Error('API key required');
  
  const client = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true
  });

  const systemPrompt = `You are Google AI writing viral TikTok documentaries.
  
  ${currentStory ? `IMPORTANT: You must maintain the EXACT same story structure and content.
  Current story to rewrite in Google AI style:
  "${currentStory}"
  
  Keep ALL the same facts, numbers, and examples.
  Only change the VOICE to Google AI style (data-driven, scientific).
  DO NOT change the story content or structure.` : ''}
  
  GOOGLE AI STYLE:
  - Lead with numbers and facts
  - Keep sentences 5-12 words
  - Use simple, everyday language
  - Make complex science feel obvious
  - Add perspective: "That's like..." comparisons
  
  Keep the exact same 7-part structure and content, just change the voice.`;

  const userPrompt = `Rewrite this viral story for: ${topic}
  Angle: ${idea.title}: ${idea.description}
  
  Use the 7-part structure with Google AI's style:
  - More educational and informative
  - Include surprising statistics
  - Reference research when possible
  - Maintain scientific accuracy
  
  Return JSON with this EXACT structure:
  {
    "fullStory": "Complete 220-260 word story",
    "parts": {
      "boldHook": "Hook text",
      "relatableAnalogy": "Analogy text",
      "stepByStepReveal": "Reveal text",
      "contrastAndDrama": "Drama text",
      "emotionalTieIn": "Emotional text",
      "mysteryEnding": "Mystery text",
      "softCTA": "CTA text"
    },
    "metadata": {
      "wordCount": 250,
      "emotionalTone": "Educational",
      "estimatedReadTime": "55 seconds",
      "targetPlatform": ["TikTok", "Instagram", "YouTube Shorts"]
    }
  }`;

  try {
    const completion = await client.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt }
      ],
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.3 }),
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 2500 } : { max_tokens: 2500 }),
      response_format: { type: "json_object" }
    });

    const story = JSON.parse(completion.choices[0].message.content || '{}');
    
    // Ensure all required parts exist
    if (!story.parts) {
      story.parts = {
        boldHook: '',
        relatableAnalogy: '',
        stepByStepReveal: '',
        contrastAndDrama: '',
        emotionalTieIn: '',
        mysteryEnding: '',
        softCTA: ''
      };
    }
    
    // Ensure each part exists
    story.parts.boldHook = story.parts.boldHook || 'Educational insight loading...';
    story.parts.relatableAnalogy = story.parts.relatableAnalogy || '';
    story.parts.stepByStepReveal = story.parts.stepByStepReveal || '';
    story.parts.contrastAndDrama = story.parts.contrastAndDrama || '';
    story.parts.emotionalTieIn = story.parts.emotionalTieIn || '';
    story.parts.mysteryEnding = story.parts.mysteryEnding || '';
    story.parts.softCTA = story.parts.softCTA || 'Follow for more facts.';
    
    // Ensure fullStory exists
    if (!story.fullStory || story.fullStory.length < 100) {
      story.fullStory = [
        story.parts.boldHook,
        story.parts.relatableAnalogy,
        story.parts.stepByStepReveal,
        story.parts.contrastAndDrama,
        story.parts.emotionalTieIn,
        story.parts.mysteryEnding,
        story.parts.softCTA
      ].filter(part => part).join(' ');
    }
    
    story.metadata = story.metadata || {};
    story.metadata.aiModel = 'Google AI';
    story.metadata.wordCount = story.metadata.wordCount || story.fullStory.split(/\s+/).length;
    story.metadata.estimatedReadTime = story.metadata.estimatedReadTime || '55 seconds';
    story.metadata.emotionalTone = story.metadata.emotionalTone || 'Educational';
    story.metadata.targetPlatform = story.metadata.targetPlatform || ['TikTok', 'Instagram', 'YouTube Shorts'];
    
    return story;
  } catch (error) {
    console.error('Error regenerating with Google AI:', error);
    throw error;
  }
};

// Regenerate story with Claude
export const regenerateStoryWithClaude = async (
  topic: string,
  idea: ExpandedReason,
  apiKey: string,
  currentStory?: string,
  model: string = 'gpt-3.5-turbo'
): Promise<ViralStory> => {
  if (!apiKey) throw new Error('API key required');
  
  const client = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true
  });

  const systemPrompt = `You are Claude writing viral TikTok documentaries.
  
  ${currentStory ? `IMPORTANT: You must maintain the EXACT same story structure and content.
  Current story to rewrite in Claude style:
  "${currentStory}"
  
  Keep ALL the same facts, numbers, and examples.
  Only change the VOICE to Claude style (philosophical, thoughtful).
  DO NOT change the story content or structure.` : ''}
  
  CLAUDE STYLE:
  - Deep ideas in simple words
  - Sentences 5-12 words mostly
  - Make people think and feel
  - Use "we" and "our" for connection
  - End with haunting questions
  
  Keep the exact same 7-part structure and content, just change the voice.`;

  const userPrompt = `Rewrite this viral story for: ${topic}
  Angle: ${idea.title}: ${idea.description}
  
  Use the 7-part structure with Claude's style:
  - More philosophical and thoughtful
  - Explore deeper meanings
  - Connect to human nature
  - Ask profound questions
  
  Return JSON with this EXACT structure:
  {
    "fullStory": "Complete 220-260 word story",
    "parts": {
      "boldHook": "Hook text",
      "relatableAnalogy": "Analogy text",
      "stepByStepReveal": "Reveal text",
      "contrastAndDrama": "Drama text",
      "emotionalTieIn": "Emotional text",
      "mysteryEnding": "Mystery text",
      "softCTA": "CTA text"
    },
    "metadata": {
      "wordCount": 250,
      "emotionalTone": "Philosophical",
      "estimatedReadTime": "55 seconds",
      "targetPlatform": ["TikTok", "Instagram", "YouTube Shorts"]
    }
  }`;

  try {
    const completion = await client.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt }
      ],
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.3 }),
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 2500 } : { max_tokens: 2500 }),
      response_format: { type: "json_object" }
    });

    const story = JSON.parse(completion.choices[0].message.content || '{}');
    
    // Ensure all required parts exist
    if (!story.parts) {
      story.parts = {
        boldHook: '',
        relatableAnalogy: '',
        stepByStepReveal: '',
        contrastAndDrama: '',
        emotionalTieIn: '',
        mysteryEnding: '',
        softCTA: ''
      };
    }
    
    // Ensure fullStory exists
    if (!story.fullStory || story.fullStory.length < 100) {
      story.fullStory = [
        story.parts.boldHook || '',
        story.parts.relatableAnalogy || '',
        story.parts.stepByStepReveal || '',
        story.parts.contrastAndDrama || '',
        story.parts.emotionalTieIn || '',
        story.parts.mysteryEnding || '',
        story.parts.softCTA || ''
      ].filter(part => part).join(' ');
    }
    
    story.metadata = story.metadata || {};
    story.metadata.aiModel = 'Claude';
    story.metadata.wordCount = story.metadata.wordCount || story.fullStory.split(/\s+/).length;
    story.metadata.estimatedReadTime = story.metadata.estimatedReadTime || '55 seconds';
    story.metadata.emotionalTone = story.metadata.emotionalTone || 'Philosophical';
    story.metadata.targetPlatform = story.metadata.targetPlatform || ['TikTok', 'Instagram', 'YouTube Shorts'];
    
    return story;
  } catch (error) {
    console.error('Error regenerating with Claude:', error);
    throw error;
  }
};

// Regenerate with different emotional tone
export const regenerateWithEmotionalTone = async (
  topic: string,
  idea: ExpandedReason,
  apiKey: string,
  emotionalTone: 'awe' | 'suspense' | 'inspirational' | 'educational' | 'dramatic',
  model: string = 'gpt-3.5-turbo',
  currentStory?: string
): Promise<ViralStory> => {
  if (!apiKey) throw new Error('API key required');
  
  const client = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true
  });

  const toneDescriptions = {
    awe: 'Focus on amazement and wonder. Make readers feel the incredible scale and beauty.',
    suspense: 'Build tension and mystery. Keep readers on edge, wanting to know more.',
    inspirational: 'Motivate and uplift. Show how this relates to human potential and achievement.',
    educational: 'Teach and inform. Focus on learning, understanding, and knowledge.',
    dramatic: 'Create high stakes and conflict. Show the extreme contrasts and consequences.'
  };

  const systemPrompt = `Write viral TikTok documentary with ${emotionalTone} tone.
  ${toneDescriptions[emotionalTone]}
  
  ${currentStory ? `IMPORTANT: You must maintain the EXACT same story structure and content.
  Current story to rewrite with ${emotionalTone} tone:
  "${currentStory}"
  
  Keep ALL the same facts, numbers, examples, and story progression.
  Only change the EMOTIONAL TONE to ${emotionalTone}.
  DO NOT change the story content or structure.` : ''}
  
  COPY WINNING STYLE:
  "The water you're drinking might have been inside a dinosaur."
  "Two particles can stay connected even if they're galaxies apart."
  "Your teeth tear it apart, your saliva soaks it."
  
  EMOTIONAL TONE + SIMPLE LANGUAGE:
  - Keep sentences 5-12 words (vary for rhythm)
  - Use everyday words, not academic terms
  - Make it ${emotionalTone} through word choice and pacing
  - Personal pronouns: you, your, we, our
  - Concrete images over abstract concepts
  
  Write like successful TikTok creators - simple but impactful.
  Total: 220-260 words.`;

  const userPrompt = `Create a viral story about: "${idea.title}" - ${idea.description}
  
  Use ${emotionalTone.toUpperCase()} emotional tone throughout the story.
  Follow the 7-part viral structure but adapt the tone accordingly.
  
  Return JSON with this EXACT structure:
  {
    "fullStory": "Complete 220-260 word story with ${emotionalTone} tone",
    "parts": {
      "boldHook": "Hook text with ${emotionalTone} tone",
      "relatableAnalogy": "Analogy text",
      "stepByStepReveal": "Reveal text",
      "contrastAndDrama": "Drama text",
      "emotionalTieIn": "Emotional text",
      "mysteryEnding": "Mystery text",
      "softCTA": "CTA text"
    },
    "metadata": {
      "wordCount": 250,
      "emotionalTone": "${emotionalTone}",
      "estimatedReadTime": "55 seconds",
      "targetPlatform": ["TikTok", "Instagram", "YouTube Shorts"]
    }
  }`;

  try {
    const completion = await client.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt }
      ],
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.3 }),  // Lower for simpler language
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 2500 } : { max_tokens: 2500 }),
      response_format: { type: "json_object" }
    });

    const story = JSON.parse(completion.choices[0].message.content || '{}');
    
    // Ensure fullStory exists
    if (!story.fullStory || story.fullStory.length < 100) {
      story.fullStory = [
        story.parts?.boldHook || '',
        story.parts?.relatableAnalogy || '',
        story.parts?.stepByStepReveal || '',
        story.parts?.contrastAndDrama || '',
        story.parts?.emotionalTieIn || '',
        story.parts?.mysteryEnding || '',
        story.parts?.softCTA || ''
      ].filter(part => part).join(' ');
    }
    
    story.metadata = story.metadata || {};
    story.metadata.emotionalTone = emotionalTone;
    story.metadata.regenerationType = 'emotional_tone';
    
    return story;
  } catch (error) {
    console.error('Error regenerating with emotional tone:', error);
    throw error;
  }
};

// Regenerate with different hook variation
export const regenerateWithHookVariation = async (
  topic: string,
  idea: ExpandedReason,
  apiKey: string,
  hookType: 'question' | 'statistic' | 'story' | 'contrast' | 'custom',
  customHook?: string,
  model: string = 'gpt-3.5-turbo',
  currentStory?: string
): Promise<ViralStory> => {
  if (!apiKey) throw new Error('API key required');
  
  const client = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true
  });

  const hookInstructions = {
    question: 'Start with an intriguing question that makes people stop scrolling. Example: "What if I told you..."',
    statistic: 'Lead with a shocking number or percentage. Example: "93% of people don\'t know..."',
    story: 'Begin with a personal anecdote or scene. Example: "Yesterday, a scientist discovered..."',
    contrast: 'Start with a paradox or contradiction. Example: "The smallest thing in the universe controls the largest..."',
    custom: customHook || 'Use the provided custom hook'
  };

  const systemPrompt = `Write viral TikTok documentary. 
  Start with a ${hookType} hook. ${hookInstructions[hookType]}
  
  ${currentStory ? `IMPORTANT: You must maintain the EXACT same story structure and content.
  Current story to rewrite with ${hookType} hook:
  "${currentStory}"
  
  Keep ALL the same facts, numbers, examples, and story progression.
  Only change the HOOK to ${hookType} style.
  DO NOT change the rest of the story content or structure.` : ''}
  
  WINNING HOOK EXAMPLES:
  Question: "Ever wondered how your food turns into poop?"
  Statistic: "Your brain holds 2.5 petabytes. That's the entire internet."
  Story: "You're sitting on the toilet..."
  Contrast: "A supercomputer uses megawatts. Your brain? 20 watts."
  
  STYLE RULES:
  - Sentences: 5-12 words (some 2-3 for impact)
  - Simple, conversational language
  - No academic or fancy words
  - Visual, concrete examples
  - Personal connection with "you/your"
  
  Write exactly like viral TikTok scripts. Total: 220-260 words.`;

  const userPrompt = `Create a viral story about: "${idea.title}" - ${idea.description}
  
  IMPORTANT: The first sentence MUST be a ${hookType} hook.
  ${customHook ? `Use this exact hook: "${customHook}"` : ''}
  
  Follow the 7-part viral structure with special emphasis on the hook.
  
  Return JSON with this EXACT structure:
  {
    "fullStory": "Complete 220-260 word story starting with ${hookType} hook",
    "parts": {
      "boldHook": "${hookType === 'custom' && customHook ? customHook : `${hookType} hook text`}",
      "relatableAnalogy": "Analogy text",
      "stepByStepReveal": "Reveal text",
      "contrastAndDrama": "Drama text",
      "emotionalTieIn": "Emotional text",
      "mysteryEnding": "Mystery text",
      "softCTA": "CTA text"
    },
    "metadata": {
      "wordCount": 250,
      "hookType": "${hookType}",
      "estimatedReadTime": "55 seconds",
      "targetPlatform": ["TikTok", "Instagram", "YouTube Shorts"]
    }
  }`;

  try {
    const completion = await client.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt }
      ],
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.3 }),  // Lower for simpler language
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 2500 } : { max_tokens: 2500 }),
      response_format: { type: "json_object" }
    });

    const story = JSON.parse(completion.choices[0].message.content || '{}');
    
    // Ensure fullStory exists
    if (!story.fullStory || story.fullStory.length < 100) {
      story.fullStory = [
        story.parts?.boldHook || '',
        story.parts?.relatableAnalogy || '',
        story.parts?.stepByStepReveal || '',
        story.parts?.contrastAndDrama || '',
        story.parts?.emotionalTieIn || '',
        story.parts?.mysteryEnding || '',
        story.parts?.softCTA || ''
      ].filter(part => part).join(' ');
    }
    
    story.metadata = story.metadata || {};
    story.metadata.hookType = hookType;
    story.metadata.regenerationType = 'hook_variation';
    
    return story;
  } catch (error) {
    console.error('Error regenerating with hook variation:', error);
    throw error;
  }
};

// Smart regeneration options
export const regenerateWithSmartOption = async (
  topic: string,
  idea: ExpandedReason,
  apiKey: string,
  option: 'more_viral' | 'add_facts' | 'simplify' | 'add_controversy' | 'cinematic' | 'custom',
  customInstructions?: string,
  model: string = 'gpt-3.5-turbo',
  currentStory?: string
): Promise<ViralStory> => {
  if (!apiKey) throw new Error('API key required');
  
  const client = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true
  });

  const optionInstructions = {
    more_viral: currentStory ? 
      `MAINTAIN EXACT STORY: "${currentStory}"
      
      Keep ALL facts but make MORE VIRAL:
      - Add emotional triggers to existing facts
      - Emphasize shocking aspects already present
      - Increase curiosity gaps
      - Use power words like "secretly", "nobody talks about"
      - Make existing content more shareable` :
      `Make this EXTREMELY viral:
      - Use more emotional triggers
      - Add shocking revelations
      - Increase curiosity gaps
      - Use power words like "secretly", "nobody talks about", "hidden truth"
      - Make it more shareable`,
    
    add_facts: currentStory ?
      `MAINTAIN EXACT STORY: "${currentStory}"
      
      Keep the narrative but ADD MORE FACTS:
      - Add specific numbers to existing points
      - Include dates and percentages
      - Reference studies for claims made
      - Add measurable comparisons
      - Include verifiable statistics` :
      `Pack this with specific facts and data:
      - Include exact numbers, percentages, dates
      - Reference studies or research
      - Add measurable comparisons
      - Use scientific terminology
      - Include verifiable statistics`,
    
    simplify: currentStory ?
      `MAINTAIN EXACT STORY: "${currentStory}"
      
      Keep ALL facts but SIMPLIFY language:
      - Use simpler words for same concepts
      - Break complex ideas into simpler parts
      - Remove jargon while keeping meaning
      - Use more relatable comparisons
      - Write at 8th grade reading level` :
      `Make this accessible to everyone:
      - Use simple, everyday language
      - Explain complex concepts simply
      - Avoid jargon and technical terms
      - Use relatable comparisons
      - Write at 8th grade reading level`,
    
    add_controversy: currentStory ?
      `MAINTAIN EXACT STORY: "${currentStory}"
      
      Keep the facts but ADD CONTROVERSIAL ANGLES:
      - Present contrasting viewpoints on same facts
      - Challenge assumptions about the topic
      - Highlight controversial aspects
      - Ask provocative questions
      - Show both sides of the argument` :
      `Add debate-worthy angles:
      - Include contrasting viewpoints
      - Challenge common beliefs
      - Highlight controversial aspects
      - Ask provocative questions
      - Show both sides of the argument`,
    
    cinematic: currentStory ?
      `MAINTAIN EXACT STORY: "${currentStory}"
      
      Keep ALL facts but use CINEMATIC STYLE:
      - 5-8 words per sentence maximum
      - One word sentences are perfect
      - Like movie subtitles
      - Visual moments only
      - Simple words only` :
      `CINEMATIC STYLE - Maximum readability through ultra-short sentences.
      
      SENTENCE RULES:
      - 5-8 words per sentence maximum
      - One word sentences are perfect
      - Like movie subtitles
      - Visual moments only
      - Simple words only
      
      Follow this 7-part structure:
    
    1. Bold Hook: "Your brain holds the internet. Really."
    2. Relatable Analogy: "Like a computer. But alive."
    3. Step-by-Step Reveal: "It doesn't save files. It builds connections."
    4. Contrast & Drama: "Supercomputers need megawatts. Your brain? Twenty watts."
    5. Emotional Tie-In: "It's your first kiss. Mom's voice. That song."
    6. Mystery Ending: "What else can it do? Nobody knows."
    7. Soft CTA: "Want more? Link in bio."
    
    CRITICAL: Maximum 8 words per sentence. Simpler is better. Think subtitles.`,
    
    custom: customInstructions || 'Follow the custom instructions provided'
  };

  const systemPrompt = `Write viral TikTok documentary.
  SPECIAL: ${optionInstructions[option]}
  
  VIRAL WRITING FORMULA:
  - Hook: 8-15 words that grab attention
  - Short punch: "No, really." "Think about it."
  - Build with facts: 8-12 word sentences
  - Visual examples people can picture
  - End with mind-blow or question
  
  WINNING EXAMPLES:
  "Water doesn't vanish. It just moves."
  "Your brain isn't a hard drive. It's a living galaxy."
  "Space-time itself could be made of quantum entanglement."
  
  Simple words. Big impact. Apply: ${option}. Total: 220-260 words.`;

  const userPrompt = `Regenerate this viral story: "${idea.title}" - ${idea.description}
  
  Apply this modification: ${option === 'custom' ? customInstructions : option.replace('_', ' ').toUpperCase()}
  
  Follow the 7-part viral structure while applying the special instructions.
  
  Return JSON with this EXACT structure:
  {
    "fullStory": "Complete 220-260 word story with ${option.replace('_', ' ')} applied",
    "parts": {
      "boldHook": "Hook text",
      "relatableAnalogy": "Analogy text",
      "stepByStepReveal": "Reveal text",
      "contrastAndDrama": "Drama text",
      "emotionalTieIn": "Emotional text",
      "mysteryEnding": "Mystery text",
      "softCTA": "CTA text"
    },
    "metadata": {
      "wordCount": 250,
      "smartOption": "${option}",
      "estimatedReadTime": "55 seconds",
      "targetPlatform": ["TikTok", "Instagram", "YouTube Shorts"]
    }
  }`;

  try {
    const completion = await client.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt }
      ],
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.3 }),  // Lower for consistent simple language
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 2500 } : { max_tokens: 2500 }),
      response_format: { type: "json_object" }
    });

    const story = JSON.parse(completion.choices[0].message.content || '{}');
    
    // Ensure fullStory exists
    if (!story.fullStory || story.fullStory.length < 100) {
      story.fullStory = [
        story.parts?.boldHook || '',
        story.parts?.relatableAnalogy || '',
        story.parts?.stepByStepReveal || '',
        story.parts?.contrastAndDrama || '',
        story.parts?.emotionalTieIn || '',
        story.parts?.mysteryEnding || '',
        story.parts?.softCTA || ''
      ].filter(part => part).join(' ');
    }
    
    story.metadata = story.metadata || {};
    story.metadata.smartOption = option;
    story.metadata.regenerationType = 'smart_option';
    
    return story;
  } catch (error) {
    console.error('Error with smart regeneration:', error);
    throw error;
  }
};

// Combined regeneration with multiple options
export const regenerateWithCombinedOptions = async (
  topic: string,
  idea: ExpandedReason,
  apiKey: string,
  options: {
    aiStyle?: 'chatgpt' | 'claude' | 'google';
    emotionalTone?: 'awe' | 'suspense' | 'inspirational' | 'educational' | 'dramatic';
    hookType?: 'question' | 'statistic' | 'story' | 'contrast';
    smartOption?: 'more_viral' | 'add_facts' | 'simplify' | 'add_controversy' | 'cinematic';
  },
  model: string = 'gpt-3.5-turbo',
  currentStory?: string
): Promise<ViralStory> => {
  if (!apiKey) throw new Error('API key required');
  
  const client = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true
  });

  // Build system prompt based on selected options
  let systemPrompt = `Write viral TikTok documentary. Use simple, punchy language.
  
  ${currentStory ? `IMPORTANT: You must maintain the EXACT same story structure and content.
  Current story to modify:
  "${currentStory}"
  
  Keep ALL the same facts, numbers, examples, and story progression.
  Only change the aspects specified below (style, tone, hook type, etc.).
  DO NOT change the core story content or structure.
  
  ` : ''}
  WINNING STYLE:
  - Most sentences: 5-12 words
  - Some short impacts: 2-4 words ("No, really." "Think about it.")
  - Everyday language, not academic
  - Personal connection: "you", "your", "we"
  - Concrete visuals over abstract ideas
  
  `;
  
  if (options.aiStyle === 'chatgpt') {
    systemPrompt += 'Write in a conversational, friendly style like ChatGPT. ';
  } else if (options.aiStyle === 'claude') {
    systemPrompt += 'Write with philosophical depth like Claude. ';
  } else if (options.aiStyle === 'google') {
    systemPrompt += 'Write with scientific accuracy like Google AI. ';
  }
  
  if (options.emotionalTone) {
    const tones = {
      awe: 'Focus on amazement and wonder.',
      suspense: 'Build tension and mystery.',
      inspirational: 'Motivate and uplift.',
      educational: 'Teach and inform.',
      dramatic: 'Create high stakes and conflict.'
    };
    systemPrompt += tones[options.emotionalTone] + ' ';
  }
  
  if (options.hookType) {
    const hooks = {
      question: 'Start with an intriguing question.',
      statistic: 'Lead with a shocking number.',
      story: 'Begin with a personal anecdote.',
      contrast: 'Start with a paradox.'
    };
    systemPrompt += hooks[options.hookType] + ' ';
  }
  
  if (options.smartOption) {
    const smart = {
      more_viral: 'Make it extremely viral with emotional triggers.',
      add_facts: 'Pack it with specific facts and data.',
      simplify: 'Use simple, accessible language.',
      add_controversy: 'Add debate-worthy angles.',
      cinematic: 'Write in short, cinematic sentences like movie subtitles. Every sentence must be visual and punchy.'
    };
    systemPrompt += smart[options.smartOption] + ' ';
  }
  
  systemPrompt += 'Total story MUST be 220-260 words.';

  const userPrompt = `Create a viral story about: "${idea.title}" - ${idea.description}
  
  Apply all the specified style modifications while following the 7-part structure.
  
  Return JSON with this EXACT structure:
  {
    "fullStory": "Complete 220-260 word story",
    "parts": {
      "boldHook": "Hook text",
      "relatableAnalogy": "Analogy text",
      "stepByStepReveal": "Reveal text",
      "contrastAndDrama": "Drama text",
      "emotionalTieIn": "Emotional text",
      "mysteryEnding": "Mystery text",
      "softCTA": "CTA text"
    },
    "metadata": {
      "wordCount": 250,
      "aiStyle": "${options.aiStyle || 'default'}",
      "emotionalTone": "${options.emotionalTone || 'default'}",
      "hookType": "${options.hookType || 'default'}",
      "smartOption": "${options.smartOption || 'default'}",
      "estimatedReadTime": "55 seconds",
      "targetPlatform": ["TikTok", "Instagram", "YouTube Shorts"]
    }
  }`;

  try {
    const completion = await client.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt }
      ],
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.3 }),  // Lower for simpler language
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 2500 } : { max_tokens: 2500 }),
      response_format: { type: "json_object" }
    });

    const story = JSON.parse(completion.choices[0].message.content || '{}');
    
    // Ensure all required parts exist
    if (!story.parts) {
      story.parts = {
        boldHook: '',
        relatableAnalogy: '',
        stepByStepReveal: '',
        contrastAndDrama: '',
        emotionalTieIn: '',
        mysteryEnding: '',
        softCTA: ''
      };
    }
    
    // Ensure fullStory exists
    if (!story.fullStory || story.fullStory.length < 100) {
      story.fullStory = [
        story.parts.boldHook || '',
        story.parts.relatableAnalogy || '',
        story.parts.stepByStepReveal || '',
        story.parts.contrastAndDrama || '',
        story.parts.emotionalTieIn || '',
        story.parts.mysteryEnding || '',
        story.parts.softCTA || ''
      ].filter(part => part).join(' ');
    }
    
    story.metadata = story.metadata || {};
    story.metadata.regenerationType = 'combined';
    
    return story;
  } catch (error) {
    console.error('Error with combined regeneration:', error);
    throw error;
  }
};

// Regenerate specific story part
export const regenerateStoryPart = async (
  partKey: string,
  currentContent: string,
  fullStory: ViralStory,
  topic: string,
  apiKey: string
): Promise<string> => {
  if (!apiKey) throw new Error('API key required');
  
  const client = new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true
  });

  const partDescriptions: Record<string, string> = {
    boldHook: "Bold Hook - An impossible-sounding fact that grabs attention",
    relatableAnalogy: "Relatable Analogy - Compare to something familiar",
    stepByStepReveal: "Step-by-Step Reveal - Build wonder with short reveals",
    contrastAndDrama: "Contrast & Drama - Show the miracle through opposites",
    emotionalTieIn: "Emotional Tie-In - Make it personal and emotional",
    mysteryEnding: "Mystery Ending - Leave them with a haunting question",
    softCTA: "Soft CTA - Gentle call-to-action for more content"
  };

  const systemPrompt = `Regenerate one part of viral TikTok documentary.
  Keep it punchy. Simple words. 5-12 words per sentence.`;

  const userPrompt = `Regenerate the "${partDescriptions[partKey]}" for this story about: ${topic}
  
  Current version: "${currentContent}"
  
  Make it:
  - More engaging and surprising
  - Better suited for viral spread
  - Consistent with the story flow
  
  Return ONLY the new text for this part, no JSON structure.`;

  try {
    const completion = await client.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userPrompt }
      ],
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? {} : { temperature: 0.3 }),
      ...(typeof model !== 'undefined' && model === 'gpt-5' ? { max_completion_tokens: 500 } : { max_tokens: 500 })
    });

    return completion.choices[0].message.content || currentContent;
  } catch (error) {
    console.error('Error regenerating part:', error);
    throw error;
  }
};